import axios from 'axios';
import { apiCache } from './cache';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with optimized settings
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 8000,
});

// Retry configuration
const MAX_RETRIES = 1;
const RETRY_DELAY = 500;

// Request queue for deduplication
const pendingRequests = new Map();

// Add auth token to requests - check both storages
api.interceptors.request.use((config) => {
  // Check localStorage first (remember me), then sessionStorage
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and response caching
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on 401 for protected routes, not during login
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      const isRegisterRequest = error.config?.url?.includes('/auth/register');
      
      // Don't clear tokens or redirect for login/register failures
      if (!isLoginRequest && !isRegisterRequest) {
        // Clear from both storages
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        // Only redirect if we're on a protected route
        const currentPath = window.location.pathname;
        if (currentPath !== '/' && currentPath !== '/login' && currentPath !== '/register') {
          window.location.href = '/';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Cached GET request helper with retry logic
const cachedGet = async (url, cacheKey = null, retries = MAX_RETRIES) => {
  const key = cacheKey || url;
  
  // Check cache first - return immediately if cached
  const cached = apiCache.get(key);
  if (cached) {
    return { data: cached, fromCache: true };
  }
  
  // Check if request is already pending
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }
  
  // Make request with retry logic
  const makeRequest = async (retriesLeft) => {
    try {
      const response = await api.get(url);
      apiCache.set(key, response.data);
      return response;
    } catch (error) {
      if (retriesLeft > 0 && (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response)) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return makeRequest(retriesLeft - 1);
      }
      throw error;
    }
  };
  
  const promise = makeRequest(retries).finally(() => {
    pendingRequests.delete(key);
  });
  
  pendingRequests.set(key, promise);
  return promise;
};

// Non-cached GET with retry logic
const retryGet = async (url, config = {}, retries = MAX_RETRIES) => {
  // Support legacy call: retryGet(url, retries) where retries is a number
  if (typeof config === 'number') {
    retries = config;
    config = {};
  }
  const makeRequest = async (retriesLeft) => {
    try {
      return await api.get(url, config);
    } catch (error) {
      if (retriesLeft > 0 && (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response)) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return makeRequest(retriesLeft - 1);
      }
      throw error;
    }
  };
  return makeRequest(retries);
};

export default api;

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => cachedGet('/auth/me', 'user_profile'),
  updateProfile: (data) => {
    apiCache.invalidate('user_profile');
    return api.put('/auth/profile', data);
  },
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
};

// Wallet - with caching for balance
export const walletAPI = {
  getWallet: () => cachedGet('/wallet', 'wallet_balance'),
  getTransactions: () => cachedGet('/wallet/transactions', 'wallet_transactions'),
  buyRMB: (amount_ngn, pin_token = null) => {
    apiCache.invalidate('wallet_balance');
    apiCache.invalidate('wallet_transactions');
    return api.post('/wallet/buy-rmb', { amount_ngn, pin_token });
  },
  sellRMB: (amount_rmb, pin_token = null) => {
    apiCache.invalidate('wallet_balance');
    apiCache.invalidate('wallet_transactions');
    return api.post('/wallet/sell-rmb', { amount_rmb, pin_token });
  },
  withdraw: (amount, pin_token = null) => {
    apiCache.invalidate('wallet_balance');
    apiCache.invalidate('wallet_transactions');
    return api.post('/wallet/withdraw', { amount, pin_token });
  },
  sendNaira: (data) => {
    apiCache.invalidate('wallet_balance');
    apiCache.invalidate('wallet_transactions');
    return api.post('/wallet/send-naira', data);
  },
  getBanks: () => api.get('/banks'),
  resolveAccount: (account_number, bank_code) => api.get(`/banks/resolve?account_number=${account_number}&bank_code=${bank_code}`),
  createDVA: () => api.post('/wallet/create-dva'),
  getDVAStatus: () => api.get('/wallet/dva-status'),
  requestAlipayTransfer: (data) => {
    apiCache.invalidate('wallet_balance');
    apiCache.invalidate('wallet_transactions');
    apiCache.invalidate('alipay_transfers');
    return api.post('/wallet/alipay-transfer', data);
  },
  getAlipayTransfers: () => cachedGet('/wallet/alipay-transfers', 'alipay_transfers'),
  getAlipayTransferDetails: (reference) => api.get(`/wallet/alipay-transfers/${reference}`),
  getTransferLimits: () => cachedGet('/wallet/transfer-limits', 'transfer_limits'),
  invalidateCache: () => {
    apiCache.invalidate('wallet_balance');
    apiCache.invalidate('wallet_transactions');
    apiCache.invalidate('alipay_transfers');
  },
  // PIN Management
  getPinStatus: () => api.get('/wallet/pin/status'),
  setupPin: (pin, confirm_pin) => api.post('/wallet/pin/setup', { pin, confirm_pin }),
  changePin: (current_pin, new_pin, confirm_new_pin) => api.post('/wallet/pin/change', { current_pin, new_pin, confirm_new_pin }),
  verifyPin: (pin) => api.post('/wallet/pin/verify', { pin }),
};

// Exchange Rate - cache for 5 minutes
export const exchangeRateAPI = {
  getRate: () => cachedGet('/exchange-rate', 'exchange_rate'),
  updateRate: (data) => {
    apiCache.invalidate('exchange_rate');
    return api.put('/admin/exchange-rate', data);
  },
};

// Shipping Rates API
export const ratesAPI = {
  getRates: () => cachedGet('/rates', 'shipping_rates'),
};

// Settings (public) - cache for longer
export const settingsAPI = {
  getPublicSettings: () => cachedGet('/settings/contact', 'public_settings'),
  getCachedSettings: () => cachedGet('/settings/cached', 'cached_settings'),
};

// Dashboard - Optimized endpoints for faster loading
export const dashboardAPI = {
  getClientDashboard: () => cachedGet('/dashboard/client', 'client_dashboard'),
  getQuickStats: () => cachedGet('/dashboard/quick-stats', 'quick_stats'),
  getActivitySummary: (days = 30) => api.get(`/dashboard/activity?days=${days}`),
  invalidateCache: () => {
    apiCache.invalidate('client_dashboard');
    apiCache.invalidate('quick_stats');
  },
};

// Global Search
export const searchAPI = {
  search: (query) => api.get(`/search?q=${encodeURIComponent(query)}`),
  query: (q) => api.get('/search', { params: { q } }),
};

// Procurement - NO caching for instant fresh data
export const procurementAPI = {
  addItem: (data) => {
    apiCache.invalidate('procurement_items');
    return api.post('/procurement/items', data);
  },
  getItems: (params = {}) => api.get('/procurement/items', { params }),
  getItemCounts: () => api.get('/procurement/items/counts'),
  getItemImage: (itemId) => api.get(`/procurement/items/${itemId}/image`),
  getPipelineSummary: () => api.get('/procurement/pipeline-summary'),
  getQuotedCount: () => api.get('/procurement/quoted-count'),  // Direct call, no cache
  checkout: (data) => {
    apiCache.invalidate('procurement_items');
    apiCache.invalidate('wallet_balance');
    return api.post('/procurement/checkout', data);
  },
  // Client can update their own items (only before paid)
  updateItem: (itemId, data) => {
    apiCache.invalidate('procurement_items');
    return api.put(`/procurement/items/${itemId}`, data);
  },
  deleteItem: (itemId) => {
    apiCache.invalidate('procurement_items');
    return api.delete(`/procurement/items/${itemId}`);
  },
  cancelItem: (itemId) => {
    apiCache.invalidate('procurement_items');
    apiCache.invalidate('wallet_balance');
    return api.post(`/procurement/items/${itemId}/cancel`);
  },
};

// Tracking
export const trackingAPI = {
  submit: (data) => api.post('/tracking/submit', data),
  getSubmissions: () => api.get('/tracking/submissions'),
  delete: (id) => api.delete(`/tracking/submissions/${id}`),
};

// Warehouse
export const warehouseAPI = {
  getItems: async () => {
    const limit = 200;
    const first = await api.get('/warehouse/items', { params: { page: 1, limit } });
    const items = first.data.items || [];
    const totalPages = first.data.total_pages || 1;
    for (let page = 2; page <= totalPages; page++) {
      const res = await api.get('/warehouse/items', { params: { page, limit } });
      items.push(...(res.data.items || []));
    }
    return { ...first, data: { ...first.data, items } };
  },
  getItemMedia: (itemId) => api.get(`/warehouse/items/${itemId}/media`),
};

// Marquee update banner
export const marqueeAPI = {
  get: () => api.get('/marquee'),
};

// PDF receipts
export const receiptsAPI = {
  download: (kind, id) => api.get(`/receipts/${kind}/${id}`, { responseType: 'blob' }),
};

// Warehouse Address
export const warehouseAddressAPI = {
  get: () => api.get('/warehouse-address'),
};

// Consolidation
export const consolidationAPI = {
  request: (data) => api.post('/consolidation/request', data),
  getRequests: () => api.get('/consolidation/requests'),
  getSettings: () => api.get('/consolidation/settings'),
  verifyPaystack: (consolidationId, reference) => api.post(`/consolidation/${consolidationId}/verify-paystack?reference=${reference}`),
  delete: (consolidationId) => api.delete(`/consolidation/${consolidationId}`),
};

// Shipping
export const shippingAPI = {
  request: (data) => api.post('/shipping/request', data),
  getRequests: () => api.get('/shipping/requests'),
  getRates: () => api.get('/shipping/rates'),
  getDestinations: () => api.get('/shipping/destinations'),
  verifyPaystack: (shipmentId, reference) => api.post(`/shipping/${shipmentId}/verify-paystack`, { reference }),
};

// Payments
export const paymentAPI = {
  getPaystackConfig: () => api.get('/config/paystack'),
  getBankDetails: () => api.get('/config/bank-details'),
  initialize: (data) => api.post('/payments/initialize', data),
  verify: (data) => api.post('/payments/verify', data),
  verifyPayment: (reference) => api.post('/payments/verify', { reference }),
  requestBankTransfer: (amount) => api.post('/payments/bank-transfer-request', { amount }),
};

// Admin
export const adminAPI = {
  getDashboard: () => retryGet('/admin/dashboard'),
  getAnalytics: () => retryGet('/admin/analytics'),
  // Users
  getUsers: () => retryGet('/admin/users'),
  getUserDashboard: (userId) => retryGet(`/admin/users/${userId}/dashboard`),
  getUserWalletTrail: (userId) => retryGet(`/admin/users/${userId}/wallet-trail`),
  getUserAnalytics: (userId) => retryGet(`/admin/users/${userId}/analytics`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  toggleUserActivation: (userId, activate) => api.post(`/admin/users/${userId}/toggle-activation`, { activate }),
  // User PIN Management
  getUserPinStatus: (userId) => api.get(`/admin/users/${userId}/pin-status`),
  resetUserPin: (userId, newPin = null) => api.post(`/admin/users/${userId}/reset-pin`, newPin ? { new_pin: newPin } : {}),
  disableUserPin: (userId) => api.post(`/admin/users/${userId}/disable-pin`),
  enableUserPin: (userId) => api.post(`/admin/users/${userId}/enable-pin`),
  // User Control Center - Full admin power
  getUserFullData: (userId) => retryGet(`/admin/users/${userId}/full-data`),
  createConsolidationForUser: (userId, data) => api.post(`/admin/users/${userId}/create-consolidation`, data),
  createShipmentForUser: (userId, data) => api.post(`/admin/users/${userId}/create-shipment`, data),
  fundUserWallet: (userId, data) => api.post(`/admin/users/${userId}/fund-wallet`, data),
  deductUserWallet: (userId, data) => api.post(`/admin/users/${userId}/deduct-wallet`, data),
  // Procurement - with retry for reliability
  getProcurement: (params = {}) => api.get('/admin/procurement', { params }),
  getProcurementCounts: (params = {}) => api.get('/admin/procurement-counts', { params }),
  warehouseScan: (data) => api.post('/admin/warehouse/scan', data, { timeout: 20000 }),
  getScanExceptions: () => api.get('/admin/warehouse/exceptions'),
  resolveScanException: (id, data) => api.post(`/admin/warehouse/exceptions/${id}/resolve`, data),
  quoteProcurement: (itemId, data) => api.put(`/admin/procurement/${itemId}/quote`, data),
  updateProcurement: (itemId, data) => api.put(`/admin/procurement/${itemId}`, data),
  updateProcurementStatus: (itemId, data) => api.put(`/admin/procurement/${itemId}/status`, data),
  deleteProcurement: (itemId) => api.delete(`/admin/procurement/${itemId}`),
  // Tracking
  getTracking: (params = {}) => api.get('/admin/tracking', { params }),
  updateTracking: (trackingId, data) => api.put(`/admin/tracking/${trackingId}`, data),
  updateTrackingStatus: (trackingId, data) => api.put(`/admin/tracking/${trackingId}/status`, data),
  deleteTracking: (trackingId) => api.delete(`/admin/tracking/${trackingId}`),
  // Warehouse
  getWarehouse: (params = {}) => api.get('/admin/warehouse', { params }),
  updateWarehouseWeight: (itemId, data) => api.put(`/admin/warehouse/${itemId}/weight`, data),
  getShippingMethods: () => api.get('/admin/shipping-methods'),
  addShippingMethod: (data) => api.post('/admin/shipping-methods', data),
  updateShippingMethod: (id, data) => api.put(`/admin/shipping-methods/${id}`, data),
  deleteShippingMethod: (id) => api.delete(`/admin/shipping-methods/${id}`),
  uploadWarehousePhoto: (itemId, data) => api.post(`/admin/warehouse/${itemId}/photo`, data),
  uploadWarehouseMedia: (itemId, data) => api.post(`/admin/warehouse/${itemId}/media`, data),
  deleteWarehouseMedia: (itemId, index) => api.delete(`/admin/warehouse/${itemId}/media/${index}`),
  getWarehouseItemMedia: (itemId) => api.get(`/admin/warehouse/${itemId}/media`),
  deleteWarehouseItem: (itemId) => api.delete(`/admin/warehouse/${itemId}`),
  // Warehouse Addresses
  getWarehouseAddresses: () => api.get('/admin/warehouse-addresses'),
  createWarehouseAddress: (data) => api.post('/admin/warehouse-addresses', data),
  updateWarehouseAddress: (id, data) => api.put(`/admin/warehouse-addresses/${id}`, data),
  deleteWarehouseAddress: (id) => api.delete(`/admin/warehouse-addresses/${id}`),
  // Shipping Destinations
  getShippingDestinations: () => api.get('/admin/shipping-destinations'),
  createShippingDestination: (data) => api.post('/admin/shipping-destinations', data),
  updateShippingDestination: (id, data) => api.put(`/admin/shipping-destinations/${id}`, data),
  deleteShippingDestination: (id) => api.delete(`/admin/shipping-destinations/${id}`),
  getProcurementItemImage: (itemId) => api.get(`/admin/procurement/${itemId}/image`),
  getClientsDirectory: (params = {}) => api.get('/admin/clients-directory', { params }),
  getSectionDirectory: (section, params = {}) => api.get('/admin/section-directory', { params: { section, ...params } }),
  // Shipments
  getShipments: (params = {}) => api.get('/admin/shipments', { params }),
  updateShipmentStatus: (shipmentId, data) => api.put(`/admin/shipments/${shipmentId}/status`, data),
  deleteShipment: (shipmentId) => api.delete(`/admin/shipments/${shipmentId}`),
  updateShipmentWeight: (shipmentId, data) => api.put(`/admin/shipments/${shipmentId}/weight`, data),
  // Consolidations
  getConsolidations: (params = {}) => api.get('/admin/consolidations', { params }),
  verifyConsolidationPayment: (consolidationId) => api.post(`/consolidation/${consolidationId}/verify-payment`),
  deleteConsolidation: (consolidationId) => api.delete(`/admin/consolidation/${consolidationId}`),
  updateConsolidationWeight: (consolidationId, data) => api.put(`/admin/consolidation/${consolidationId}/weight`, data),
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  testEmail: () => api.post('/admin/test-email'),
  testTelegram: () => api.post('/admin/test-telegram'),
  // Agents
  getAgents: () => api.get('/admin/agents'),
  approveAgent: (agentId) => api.put(`/admin/agents/${agentId}/approve`),
  rejectAgent: (agentId) => api.put(`/admin/agents/${agentId}/reject`),
  processAgentPayout: (agentId, data) => api.post(`/admin/agents/${agentId}/payout`, data),
  // Bank Transfers
  getBankTransfers: (params = {}) => api.get('/admin/bank-transfers', { params }),
  approveBankTransfer: (reference) => api.post(`/admin/bank-transfers/${reference}/approve`),
  rejectBankTransfer: (reference) => api.post(`/admin/bank-transfers/${reference}/reject`),
  // Alipay Transfers
  getAlipayTransfers: (params = {}) => api.get('/admin/alipay-transfers', { params }),
  getAlipayTransferDetails: (reference) => api.get(`/admin/alipay-transfers/${reference}`),
  updateAlipayStatus: (reference, status, progress, admin_notes) => 
    api.put(`/admin/alipay-transfers/${reference}/status?status=${status}&progress=${progress}${admin_notes ? `&admin_notes=${encodeURIComponent(admin_notes)}` : ''}`),
  uploadAlipayReceipt: (reference, receipt_image) => 
    api.post(`/admin/alipay-transfers/${reference}/receipt`, receipt_image, { headers: { 'Content-Type': 'application/json' } }),
  // Withdrawals (Refunds)
  getWithdrawals: () => api.get('/admin/withdrawals'),
  approveWithdrawal: (id) => api.post(`/admin/withdrawals/${id}/approve`),
  rejectWithdrawal: (id) => api.post(`/admin/withdrawals/${id}/reject`),
  // Ecommerce
  getEcommerceCategories: () => api.get('/admin/ecommerce/categories'),
  createCategory: (data) => api.post('/admin/ecommerce/categories', data),
  updateCategory: (id, data) => api.put(`/admin/ecommerce/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/ecommerce/categories/${id}`),
  getEcommerceProducts: () => api.get('/admin/ecommerce/products'),
  createProduct: (data) => api.post('/admin/ecommerce/products', data),
  updateProduct: (id, data) => api.put(`/admin/ecommerce/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/ecommerce/products/${id}`),
  bulkUploadProducts: (products) => api.post('/admin/ecommerce/products/bulk', products),
  activateProduct: (id) => api.post(`/admin/ecommerce/products/${id}/activate`),
  deactivateProduct: (id) => api.post(`/admin/ecommerce/products/${id}/deactivate`),
  getDraftProducts: () => api.get('/admin/ecommerce/drafts'),
  // CJDropshipping / Import
  getCJStatus: () => api.get('/admin/ecommerce/cj/status'),
  getCJCategories: () => api.get('/admin/ecommerce/cj/categories'),
  importFetch: (data) => api.post('/admin/ecommerce/import/fetch', data),
  importSave: (products) => api.post('/admin/ecommerce/import/save', products),
  // Legacy 1688 endpoints (still work)
  import1688Fetch: (url, markup) => api.post('/admin/ecommerce/import-1688/fetch', { url, price_markup_percent: markup }),
  import1688Save: (products) => api.post('/admin/ecommerce/import-1688/save', products),
  getEcommerceBanners: () => api.get('/admin/ecommerce/banners'),
  createBanner: (data) => api.post('/admin/ecommerce/banners', data),
  updateBanner: (id, data) => api.put(`/admin/ecommerce/banners/${id}`, data),
  deleteBanner: (id) => api.delete(`/admin/ecommerce/banners/${id}`),
  getEcommerceOrders: () => api.get('/admin/ecommerce/orders'),
  // Training Management
  getTrainingPosts: () => api.get('/admin/training/posts'),
  createTrainingPost: (data) => api.post('/admin/training/posts', data),
  updateTrainingPost: (id, data) => api.put(`/admin/training/posts/${id}`, data),
  deleteTrainingPost: (id) => api.delete(`/admin/training/posts/${id}`),
  uploadTrainingMedia: (formData) => api.post('/admin/training/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Bulk Operations
  bulkStatusUpdate: (itemIds, newStatus, entityType, notes = null) => api.post('/admin/bulk-status-update', {
    item_ids: itemIds,
    new_status: newStatus,
    entity_type: entityType,
    notes
  }),
  bulkDelete: (itemIds, entityType) => api.post('/admin/bulk-delete', {
    item_ids: itemIds,
    entity_type: entityType
  }),
};

// Ecommerce API (User-facing)
export const ecommerceAPI = {
  // Guest checkout (payment-first)
  guestCheckoutInit: (data) => api.post('/ecommerce/guest-checkout', data),
  guestCheckoutVerify: (reference) => api.post(`/ecommerce/guest-checkout/verify?reference=${reference}`),
  getCategories: () => api.get('/ecommerce/categories'),
  getBanners: () => api.get('/ecommerce/banners'),
  getProducts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/ecommerce/products${query ? `?${query}` : ''}`);
  },
  getProduct: (id) => api.get(`/ecommerce/products/${id}`),
  getFeaturedProducts: () => api.get('/ecommerce/featured'),
  getCart: () => api.get('/ecommerce/cart'),
  addToCartBulk: (items) => api.post('/ecommerce/cart/add-bulk', { items }),
  addToCart: (productId, quantity, variantSelections = null, shippingMethod = null) => api.post('/ecommerce/cart/add', {
    product_id: productId,
    quantity,
    variant_selections: variantSelections,
    shipping_method: shippingMethod
  }),
  updateCartItem: (productId, quantity, variantSelections) => api.put('/ecommerce/cart/update', { product_id: productId, quantity, variant_selections: variantSelections }),
  clearCart: () => api.delete('/ecommerce/cart/clear'),
  checkout: (data) => api.post('/ecommerce/checkout', data),
  downloadVirtual: (itemId) => api.get(`/ecommerce/download/${itemId}`),
  initPaystackCheckout: (data) => api.post('/ecommerce/checkout/paystack-init', data || {}),
  getSiteSettings: () => api.get('/site-settings'),
  verifyPaystackCheckout: (reference) => api.post(`/ecommerce/checkout/paystack-verify?reference=${reference}`),
  getOrders: () => api.get('/ecommerce/orders'),
  getTrending: (limit = 8) => api.get(`/ecommerce/trending?limit=${limit}`),
};

// Training/Blog API
export const trainingAPI = {
  getPosts: (category = null) => api.get(`/training/posts${category ? `?category=${category}` : ''}`),
  getPost: (slug) => api.get(`/training/posts/${slug}`),
  getCategories: () => api.get('/training/categories'),
};

// Guide API
export const guideAPI = {
  getSections: () => api.get('/guide/sections'),
};

// Agent API
export const agentAPI = {
  getDashboard: () => api.get('/agent/dashboard'),
  getCustomers: () => api.get('/agent/customers'),
  createCustomer: (data) => api.post('/agent/customers', data),
  getCustomerOrders: (email) => api.get(`/agent/customers/${email}/orders`),
  createOrder: (data) => api.post('/agent/orders', data),
  getCommissions: () => api.get('/agent/commissions'),
};

// Notifications
export const notificationAPI = {
  getNotifications: () => api.get('/notifications'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
};

// Contact/WhatsApp settings
export const contactAPI = {
  getSettings: () => cachedGet('/settings/contact', 'contact_settings'),
};

// Sourcing (Chinese procurement search)
export const sourcingAPI = {
  getConfig: () => api.get('/sourcing/config'),
  searchText: (keyword, page = 1) => api.post('/sourcing/search', { keyword, page }, { timeout: 45000 }),
  searchImage: (image_base64) => api.post('/sourcing/search-image', { image_base64 }, { timeout: 45000 }),
  parseLink: (url) => api.post('/sourcing/parse-link', { url }, { timeout: 50000 }),
  getProductDetail: (id, platform, source_url) => api.post('/sourcing/product-detail', { id, platform, source_url }, { timeout: 45000 }),
  directOrder: (data) => api.post('/sourcing/direct-order', data),
  freightEstimate: (data) => api.post('/sourcing/freight-estimate', data),
  getTrending: () => api.get('/sourcing/trending'),
};
